// Client-side mirror of the backend proposal_targets._validate_reference_range
// (services/core/src/proposal_targets.py). Keep the two in lock-step: the server
// re-validates on submit, but the console disables "Request integration" and
// shows the reason when the proposed entries don't pass here.

export type RangeEntry = {
  sex?: string;
  age_group?: string;
  unit?: string;
  range_low?: number | null;
  range_high?: number | null;
  range_optimal_low?: number | null;
  range_optimal_high?: number | null;
  range_critical_low?: number | null;
  range_critical_high?: number | null;
  notes?: string | null;
  source?: string | null;
  [k: string]: unknown;
};

// Canonical band order, non-decreasing — must match _BAND_ORDER server-side.
const BAND_ORDER = [
  'range_critical_low',
  'range_low',
  'range_optimal_low',
  'range_optimal_high',
  'range_high',
  'range_critical_high',
] as const;

const SEXES = ['all', 'male', 'female'];
const AGE_GROUPS = ['adult', 'paediatric', 'neonate', 'elderly'];

function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Returns null when the entries are well-formed, else the first error message
 *  (the same failures the server raises). */
export function validateRangeEntries(entries: RangeEntry[]): string | null {
  if (!Array.isArray(entries) || entries.length === 0) {
    return 'Add at least one range entry.';
  }
  const seen = new Set<string>();
  let i = 0;
  for (const e of entries) {
    i += 1;
    const where = `Entry ${i}`;
    if (!SEXES.includes(String(e.sex))) {
      return `${where}: sex must be one of all, male, female.`;
    }
    if (e.age_group != null && !AGE_GROUPS.includes(String(e.age_group))) {
      return `${where}: age group must be one of ${AGE_GROUPS.join(', ')}.`;
    }
    if (!String(e.unit ?? '').trim()) {
      return `${where}: unit is required.`;
    }
    for (const req of ['range_low', 'range_high'] as const) {
      if (!isNumber(e[req])) {
        return `${where}: ${req.replace('range_', 'range ')} is required and must be a number.`;
      }
    }
    for (const opt of ['range_optimal_low', 'range_optimal_high', 'range_critical_low', 'range_critical_high'] as const) {
      if (e[opt] != null && !isNumber(e[opt])) {
        return `${where}: ${opt.replace('range_', 'range ')} must be a number.`;
      }
    }
    const present = BAND_ORDER.filter((k) => isNumber(e[k])).map((k) => [k, e[k] as number] as const);
    for (let j = 0; j < present.length - 1; j++) {
      const a = present[j]!;
      const b = present[j + 1]!;
      if (a[1] > b[1]) return `${where}: ${a[0]} (${a[1]}) must be ≤ ${b[0]} (${b[1]}).`;
    }
    const key = `${e.sex}:${e.age_group ?? 'adult'}`;
    if (seen.has(key)) return `${where}: duplicate entry for sex ${e.sex}.`;
    seen.add(key);
  }
  return null;
}
